import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useAnalyticsEvent, useAnalyticsPageView } from '@chilecompra/react-kit/analytics'
import { formatDNI, useEffectOnce } from '@chilecompra/react-kit/utils'
import { Button, useCheckBox, useViewport } from '@chilecompra/react-kit/components'
import { Input, putCookie, Toast, Typography, useInput } from '@chilecompra/react-kit'
import {
  isLocalEnv,
  REACT_APP_FEATURE_FLAG_DNI_TEST_INPUT_ENABLED,
  REACT_APP_FEATURE_FLAG_RECAPTCHA_ENABLED,
  REACT_APP_TERMS_CONDITIONS_URL,
  REACT_APP_UNIQUE_PASS_SEED,
  REACT_APP_UNIQUE_PASS_URL
} from '../../../config/settings/environment'
import { useCsrfToken } from '../../../modules/hooks/security'
import { onGetUserInfoThunk, onSignInUniquePassThunk } from '../../UniquePassAuth/UniquePassAuth.actions'
import { initiateFlowThunk, onRegisterBy } from '../RegisterWizard.actions'
import ProtectByFeatures from '../../../components/Security/ProtectByFeatures'
import {
  CheckBox,
  Conditions,
  DesktopNavigation,
  Hidden,
  Image,
  Instructions,
  Link,
  MobileButtons,
  MobileNavigation,
  Root,
  Section,
  TermsLink,
  Title,
  ToastContainer
} from './RegisterWizardUniquePass.styles'
import { onGetGoogleRecaptchaTokenThunk } from '../../Recaptcha/recaptcha.actions'
import { useRecaptcha } from '../../../modules/hooks/recaptcha'

/**
 * The RegisterWizardUniquePass's container.
 */
const RegisterWizardUniquePass = props => {
  const { onChangeStep } = props
  const dispatch = useDispatch()
  const { value: checked, onChange: handleChange } = useCheckBox()
  const { value: dniValue, onChange: handleChangeDniValue } = useInput({
    initialValue: '',
    formatCallbacks: [formatDNI]
  })
  const { size } = useViewport()
  const csrfToken = useCsrfToken(REACT_APP_UNIQUE_PASS_SEED)
  const { executeRecaptcha } = useRecaptcha()
  const { token } = useSelector(state => state.recaptcha)
  const { flow: flowState } = useSelector(state => state.registerWizard)

  const handleBack = () => onChangeStep('welcome')

  const renderClaveUnicaSection = () => {
    return (
      <Typography tabIndex="0" variant="body1" fontSize="14px" marginTop="15px" fontWeight="bold" textAlign="left">
        Para comenzar el proceso de registro ingresa con tu Clave Única:
      </Typography>
    )
  }

  const renderIfYouDontHaveUniqueKeySection = () => {
    return (
      <Typography tabIndex="0" fontSize="14px" lineHeight="16px" marginTop="30px" textAlign="left" color="black1">
        Si aún no tienes tu clave, revisa cómo{' '}
        <Link
          tabIndex="0"
          aria-label="obtener tu Clave Única"
          color="primary"
          href={REACT_APP_UNIQUE_PASS_URL}
          rel="noopener noreferrer"
          target="_blank"
          underline
        >
          obtener tu Clave Única.
        </Link>
      </Typography>
    )
  }

  const renderToastSection = () => {
    return (
      <ToastContainer role="alert" aria-live="assertive">
        <Toast
          tabIndex="0"
          disableIcon
          severity="default"
          width={size.isTiny ? '100%' : size.isDownToLarge ? '100%' : '231px'}
        >
          Mercado Público utiliza tu Clave Única para validar tu identidad digital.
        </Toast>
      </ToastContainer>
    )
  }

  const renderTestInput = () => {
    return (
      <Input
        value={dniValue}
        onChange={handleChangeDniValue}
        margin="15px 0 0 0"
        placeholder="Ingresa RUT para testear"
        aria-label="Rut test"
      />
    )
  }

  const handleNext = () => {
    if (!isLocalEnv) {
      dispatch(onSignInUniquePassThunk({ csrfToken }))
      putCookie('dniTestCookie', dniValue, { base64: true, json: true })
    } else {
      handleRegisterByUniquePass()
    }
  }

  const disabledUniqueKey = !checked

  useAnalyticsEvent({ action: 'Inicia registro por ClaveÚnica', category: 'registrar-usuario' })
  useAnalyticsPageView('/paso/1/clave_unica')

  const handleRegisterByUniquePass = () => {
    if (
      REACT_APP_FEATURE_FLAG_DNI_TEST_INPUT_ENABLED === true ||
      REACT_APP_FEATURE_FLAG_DNI_TEST_INPUT_ENABLED === 'true'
    ) {
      putCookie('dniTestCookie', dniValue, { base64: true, json: true })
      batch(() => {
        dispatch(onGetUserInfoThunk({ token: csrfToken }))
        dispatch(onRegisterBy('by-unique-pass'))
      })
    }
    onChangeStep('personal-info')
  }

  useEffectOnce(() => {
    if (REACT_APP_FEATURE_FLAG_RECAPTCHA_ENABLED === true || REACT_APP_FEATURE_FLAG_RECAPTCHA_ENABLED === 'true') {
      dispatch(
        onGetGoogleRecaptchaTokenThunk({
          executeRecaptcha,
          skipped: true
        })
      )
    } else {
      dispatch(initiateFlowThunk(token))
    }
  }, [dispatch, executeRecaptcha])

  useEffect(() => {
    if (token) {
      dispatch(initiateFlowThunk(token))
    }
  }, [token, dispatch])

  return (
    <Root>
      <Section container>
        <Section item xs={12} md={7} sm={6}>
          <Title tabIndex="0" aria-label="Paso 1: Comienza el proceso de registro">
            Comienza el proceso de registro
          </Title>
          <Instructions tabIndex="0">
            Para continuar, te pedimos que leas y aceptes los siguientes&nbsp;
            <Link
              tabIndex="0"
              aria-label="términos y condiciones"
              color="primary"
              href={REACT_APP_TERMS_CONDITIONS_URL}
              rel="noopener noreferrer"
              target="_blank"
              underline
            >
              términos y condiciones
            </Link>
            .&nbsp;Luego, haz clic en el botón de Clave Única para ingresar con tus credenciales al portal del Registro
            Civil.
          </Instructions>
          {size.isDownToLarge && !size.isTiny && renderClaveUnicaSection()}
          {size.isTiny && renderClaveUnicaSection()}
          <Conditions>
            <CheckBox
              tabIndex="0"
              aria-label="Acepto terminos y condiciones de uso"
              checked={checked}
              onChange={handleChange}
              color="primary"
            />
            Acepto los&nbsp;
            <TermsLink
              tabIndex="0"
              aria-label="términos y condiciones de uso"
              href={REACT_APP_TERMS_CONDITIONS_URL}
              rel="noopener noreferrer"
              target="_blank"
            >
              términos y condiciones de uso
            </TermsLink>
          </Conditions>
          {!flowState?.loading && ![null, undefined, ''].includes(flowState?.error) && (
            <ToastContainer>
              <Toast tabIndex="0" severity="warning">
                Estamos experimentando problemas técnicos. Puedes intentar nuevamente en unos minutos.
              </Toast>
            </ToastContainer>
          )}
          {!size.isTiny && !size.isDownToLarge && renderClaveUnicaSection()}
          {size.isTiny && !size.isDownToLarge && renderIfYouDontHaveUniqueKeySection()}
          {size.isDownToLarge && renderIfYouDontHaveUniqueKeySection()}
          {(size.isTiny || size.isDownToLarge) && renderToastSection()}
          {!size.isTiny && !size.isDownToLarge && (
            <Section item xs={12}>
              <ProtectByFeatures rule={features => features['com.buyer/dni.test.input']}>
                {renderTestInput()}
              </ProtectByFeatures>
              <DesktopNavigation>
                <Button
                  tabIndex="0"
                  aria-label="Cancelar"
                  onClick={handleBack}
                  type="button"
                  variant="text"
                  width="100%"
                >
                  Cancelar
                </Button>
                <Button
                  tabIndex="0"
                  aria-label="Ir a ClaveÚnica"
                  loading={flowState?.loading}
                  color="primary"
                  disabled={disabledUniqueKey || !flowState?.data?.flowId}
                  type="button"
                  width="100%"
                  onClick={handleNext}
                >
                  <Image alt="Ir a ClaveÚnica" height="20px" src="/assets/img/logo-clave-unica.svg" />
                </Button>
              </DesktopNavigation>
              {!size.isTiny && !size.isDownToLarge && renderIfYouDontHaveUniqueKeySection()}
            </Section>
          )}
          {(size.isTiny || size.isDownToLarge) && (
            <MobileNavigation>
              <ProtectByFeatures rule={features => features['com.buyer/dni.test.input']}>
                {renderTestInput()}
              </ProtectByFeatures>
              <MobileButtons
                tabIndex="0"
                aria-label="Ir a ClaveÚnica"
                loading={flowState?.loading}
                color="primary"
                disabled={disabledUniqueKey || !flowState?.data?.flowId}
                type="button"
                onClick={handleNext}
              >
                <Image alt="Ir a ClaveÚnica" height="20px" src="/assets/img/logo-clave-unica.svg" />
              </MobileButtons>
              <MobileButtons tabIndex="0" aria-label="Cancelar" onClick={handleBack} type="button" variant="outlined">
                Cancelar
              </MobileButtons>
            </MobileNavigation>
          )}
        </Section>
        <Section
          item
          xs={12}
          md={5}
          sm={6}
          margin={size.isDownToLarge ? '25px 0 0' : '-25px 0 0'}
          padding={size.isTiny ? '' : '0 0 0 21px'}
        >
          <Hidden smDown>
            <Image alt="Ilustración Registro vía ClaveÚnica" src="/assets/img/steps/paso1.svg" />
          </Hidden>
          {!size.isTiny && !size.isDownToLarge && renderToastSection()}
        </Section>
      </Section>
    </Root>
  )
}

RegisterWizardUniquePass.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardUniquePass.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardUniquePass
